import { AxiosResponse } from "axios";
import { oc } from "ts-optchain";
import { Nullable } from "@/types";

import { DealernetApi } from "./DealernetApi";
import { TmsService } from "./TmsService";

const CONTROLLER_PATH = "SmartEquip";

export class SmartEquipService {

  // Get SmartEquip Truck Types
  public static async GetTruckTypes(userId: number, brandId: number, productGroupId?: Nullable<number>): Promise<ISmartEquipProductGroupsResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/TruckTypes`,
      data: { userId, brandId, productGroupId },
    });

    return oc(response as AxiosResponse<ISmartEquipProductGroupsResponseDto>).data()!;
  }

  // Get SmartEquip Models
  public static async GetModels(userId: number, brandId: number, productGroupId?: Nullable<number>): Promise<ISmartEquipProductsResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Models`,
      data: { userId, brandId, productGroupId },
    });
    return oc(response as AxiosResponse<ISmartEquipProductsResponseDto>).data()!;
  }

  // Get SmartEquip Component Groups
  public static async GetComponentGroups(userId: number, brandId: number, productId: string): Promise<ISmartEquipComponentGroupsResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/ComponentGroups`,
      data: { userId, brandId, productId },
    });
    return oc(response as AxiosResponse<ISmartEquipComponentGroupsResponseDto>).data()!;
  }

  // Get SmartEquip Components
    public static async GetComponents(userId: number, brandId: number, componentGroupName: string): Promise<ISmartEquipComponentsResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Components`,
      data: { userId, brandId, componentGroupName },
    });

    return oc(response as AxiosResponse<ISmartEquipComponentsResponseDto>).data()!;
  }


  // Get SmartEquip Parts
  public static async GetParts(userId: number, brandId: number, componentNumber: string): Promise<ISmartEquipComponentPartsResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Parts`,
      data: { userId, brandId, componentNumber },
    });

    return oc(response as AxiosResponse<ISmartEquipComponentPartsResponseDto>).data()!;
  }

  // Get SmartEquip Diagram
  public static async GetDiagram(userId: number, brandId: number, componentNumber: string, diagramFormat?: Nullable<SmartEquipDiagramFormat>): Promise<ISmartEquipComponentDiagramResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Diagram`,
      data: { userId, brandId, componentNumber, diagramFormat },
    });

    return oc(response as AxiosResponse<ISmartEquipComponentDiagramResponseDto>).data()!;
  }
}

// Nullable type helper
type Nullable<T> = T | null;

// ============================================================================
// Enums
// ============================================================================

export enum SmartEquipDiagramFormat {
  Png = 'png',
  Svg = 'svg'
}

export enum SmartEquipErrorType {
  User = 'User',
  Temp = 'Temp',
  System = 'System'
}

// ============================================================================
// Common/Shared Interfaces
// ============================================================================

export interface ISmartEquipHeaderDto {
  UUID: string;
  accountNumber: string;
  locationId: string;
  language: string;
}

export interface ISmartEquipErrorDto {
  Type: SmartEquipErrorType;
  Message: string;
}

export interface ISmartEquipBaseRequestDto {
  userId: number;
}

export interface ISmartEquipBaseResponseDto {
  statusCode: number;
  responseHeader: ISmartEquipHeaderDto;
  error?: Nullable<ISmartEquipErrorDto>;
}

// ============================================================================
// Request Interfaces
// ============================================================================

export interface ISmartEquipProductGroupsRequestDto extends ISmartEquipBaseRequestDto {
  brandId: number;
  productGroupId?: Nullable<number>;
}

export interface ISmartEquipProductsRequestDto extends ISmartEquipBaseRequestDto {
  brandId: number;
  productGroupId?: Nullable<number>;
}

export interface ISmartEquipComponentGroupsRequestDto extends ISmartEquipBaseRequestDto {
  brandId: number;
  productId: string;
}

export interface ISmartEquipComponentsRequestDto extends ISmartEquipBaseRequestDto {
  brandId: number;
  componentGroupName: string;
}

export interface ISmartEquipComponentPartsRequestDto extends ISmartEquipBaseRequestDto {
  brandId: number;
  componentNumber: string;
}

export interface ISmartEquipComponentDiagramRequestDto extends ISmartEquipBaseRequestDto {
  brandId: number;
  componentNumber: string;
  diagramFormat?: Nullable<SmartEquipDiagramFormat>;
}

// ============================================================================
// Response DTOs (List Items)
// ============================================================================

export interface ISmartEquipProductGroupDto {
  TruckClass: string;
  TruckClassId: number;
}

export interface ISmartEquipProductDto {
  ModelIdAndSerialNumberRangeId: string;
  ModelName: string;
  SerialNumberStart: string;
  SerialNumberEnd: string;
}

export interface ISmartEquipComponentGroupDto {
  PublicationIdAndDataSource: string;
  PublicationTitle: string;
}

export interface ISmartEquipComponentDto {
  PublicationIdAndDataSource: string;
  PublicationTitle: string;
  HasChildComponents: boolean;
  ChildComponents: ISmartEquipComponentDto[];
}

export interface ISmartEquipComponentPartDto {
  OemPartCode: string;
  PartDescription: string;
  Quantity: string;
  ReferenceNumber: string;
  Notes: string;
}

// ============================================================================
// Response Interfaces (Root)
// ============================================================================

export interface ISmartEquipProductGroupsResponseDto extends ISmartEquipBaseResponseDto {
  ProductGroups: ISmartEquipProductGroupDto[];
}

export interface ISmartEquipProductsResponseDto extends ISmartEquipBaseResponseDto {
  Products: ISmartEquipProductDto[];
}

export interface ISmartEquipComponentGroupsResponseDto extends ISmartEquipBaseResponseDto {
  ComponentGroups: ISmartEquipComponentGroupDto[];
}

export interface ISmartEquipComponentsResponseDto extends ISmartEquipBaseResponseDto {
  Components: ISmartEquipComponentDto[];
}

export interface ISmartEquipComponentPartsResponseDto extends ISmartEquipBaseResponseDto {
  ComponentParts: ISmartEquipComponentPartDto[];
}

export interface ISmartEquipComponentDiagramResponseDto extends ISmartEquipBaseResponseDto {
  DiagramData: string;
  DiagramFormat: SmartEquipDiagramFormat;
}

